import { useState } from 'react'
import { motion } from 'framer-motion'

/**
 * PackageCard - Reusable Luxury Package Card Component
 * 
 * Features:
 * - Hover effects: scale 1.03, elevate with shadow-glow, reveal hidden bullet
 * - Highlight mode: larger scale (1.05), permanent glow halo, centered position
 * - Custom bullet points with golden dots
 * - Package-specific CTA microcopy
 * - Max card badge overlay for limited intake
 * 
 * Props:
 * - title: Package name (Standard, Pro, Max)
 * - tagline: Optional tagline text
 * - bullets: Array of feature bullets
 * - ctaLabel: CTA button text
 * - highlight: Boolean for Standard card emphasis
 * - onCTAClick: Callback when CTA is clicked
 * 
 * Animation Details:
 * - Hover scale: 0.35s transition with ease-out
 * - Glow effect: 0.3s transition
 * - Hidden bullet reveal: 0.4s staggered animation
 */

interface PackageCardProps {
  title: string
  tagline?: string
  bullets: string[]
  ctaLabel: string
  highlight?: boolean
  onCTAClick: (title: string) => void
}

export default function PackageCard({
  title,
  tagline,
  bullets,
  ctaLabel,
  highlight = false,
  onCTAClick
}: PackageCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [showExtraBullet, setShowExtraBullet] = useState(false)
  
  // Extra bullet for hover reveal (only for Pro and Max)
  const extraBullets = {
    Pro: "Priority support & dedicated project manager",
    Max: "Exclusive access to premium design resources"
  }
  
  const handleMouseEnter = () => {
    setIsHovered(true)
    if (title === 'Pro' || title === 'Max') {
      setTimeout(() => setShowExtraBullet(true), 200)
    }
  }
  
  const handleMouseLeave = () => {
    setIsHovered(false)
    setShowExtraBullet(false)
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`
        relative w-full max-w-sm mx-auto h-full
        ${highlight ? 'card-luxury-highlight' : 'card-luxury'}
        ${isHovered ? 'scale-103' : ''}
        transition-all duration-350 ease-out
        ${highlight ? 'scale-105' : ''}
      `}
      whileHover={{ 
        scale: highlight ? 1.08 : 1.03,
        boxShadow: "0 0 40px rgba(212, 175, 55, 0.4)"
      }}
    >
      {/* Max Card Badge */}
      {title === 'Max' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="absolute -top-3 -right-3 bg-luxury-gold text-luxury-black px-3 py-1 rounded-full text-xs font-semibold z-10"
        >
          Limited intake
        </motion.div>
      )}
      
      {/* Card Content */}
      <div className="p-8 h-full flex flex-col">
        {/* Title */}
        <motion.h3 
          className="text-2xl font-bold text-luxury-gold mb-2"
          whileHover={{ scale: 1.02 }}
          style={{
            fontFamily: title.includes('تطوير') || title.includes('التسويق') || title.includes('التصميم') 
              ? "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif" 
              : "'Inter', 'Helvetica Neue', sans-serif",
            textAlign: title.includes('تطوير') || title.includes('التسويق') || title.includes('التصميم') ? 'right' : 'left'
          }}
        >
          {title}
        </motion.h3>
        
        {/* Tagline */}
        {tagline && (
          <motion.p 
            className="text-gray-400 text-sm italic mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{
              fontFamily: tagline.includes('نطور') || tagline.includes('نسوق') || tagline.includes('نبتكر') 
                ? "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif" 
                : "'Inter', 'Helvetica Neue', sans-serif",
              textAlign: tagline.includes('نطور') || tagline.includes('نسوق') || tagline.includes('نبتكر') ? 'right' : 'left'
            }}
          >
            {tagline}
          </motion.p>
        )}
        
        {/* Bullets - Flex grow to fill space */}
        <motion.ul className="space-y-3 mb-8 flex-grow">
          {bullets.map((bullet, index) => {
            // Detect Arabic content by checking for Arabic characters
            const isArabic = /[\u0600-\u06FF]/.test(bullet);
            
            return (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                className={`text-gray-300 ${isArabic ? 'text-right' : 'flex items-start'}`}
                style={{
                  direction: isArabic ? 'rtl' : 'ltr'
                }}
              >
                {!isArabic && <span className="text-luxury-gold mt-1 text-sm mr-3">•</span>}
                <span 
                  className={`text-sm leading-relaxed ${isArabic ? 'text-right' : 'text-left'}`}
                  style={{
                    fontFamily: isArabic 
                      ? "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif" 
                      : "'Inter', 'Helvetica Neue', sans-serif"
                  }}
                >
                  {bullet}
                </span>
              </motion.li>
            );
          })}
          
          {/* Extra bullet for hover reveal */}
          {showExtraBullet && (title === 'Pro' || title === 'Max') && (
            <motion.li
              initial={{ opacity: 0, x: -20, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="text-luxury-gold font-medium text-right"
              style={{
                direction: 'rtl'
              }}
            >
              <span className="text-sm leading-relaxed">
                {extraBullets[title as keyof typeof extraBullets]}
              </span>
            </motion.li>
          )}
        </motion.ul>
        
        {/* CTA Button - Always at bottom */}
        <motion.button
          onClick={() => onCTAClick(title)}
          className="w-full btn-luxury-secondary text-sm py-3 mt-auto"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            fontFamily: ctaLabel.includes('ناقش') 
              ? "'Amiri', 'Scheherazade New', 'Noto Naskh Arabic', serif" 
              : "'Inter', 'Helvetica Neue', sans-serif",
            textAlign: ctaLabel.includes('ناقش') ? 'center' : 'center'
          }}
        >
          {ctaLabel}
        </motion.button>
      </div>
      
      {/* Hover Glow Effect */}
      {isHovered && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 rounded-xl bg-luxury-gold/5 pointer-events-none"
        />
      )}
    </motion.div>
  )
}
